import {
  buildObjectiveBundle,
  buildObjectiveBundleStackedPlan,
  getLatestObjectiveBundle,
  getObjectiveBundle,
  getObjectiveGroup,
  getObjectiveGroups,
  getObjectiveSquads,
  markObjectiveSquadsSaved,
  revealObjectiveBundle,
  saveObjectiveBundleProgress,
  setObjectiveBlockMode,
  setObjectiveBundleView,
  startObjectiveSquads,
} from "@/api/api";
import {
  PayloadAction,
  createAsyncThunk,
  createSelector,
  createSlice,
} from "@reduxjs/toolkit";
import type {
  ObjectiveBundle,
  ObjectiveCategory,
  ObjectiveGroup,
  ObjectiveGroupDetail,
  ObjectiveLiveEvent,
  ObjectivePlanView,
  ObjectiveSquadRun,
  ObjectiveSquadSettings,
} from "@repo/shared/api";
import { backendErrorCode } from "@repo/shared/i18n";
import { includesIgnoringAccents } from "@repo/shared/utils";

import { APIStatus } from "../../api/APIStatus.type";
import type { AppDispatch, IRootState } from "../store";

type ObjectivesState = {
  /** `progressUpdatedAt`: the user's last objective progress sync, if any. */
  groups: {
    data: ObjectiveGroup[];
    liveEvents: ObjectiveLiveEvent[];
    categories: ObjectiveCategory[];
    status: APIStatus;
    progressUpdatedAt?: string;
  };
  currentGroup: { data?: ObjectiveGroupDetail; status: APIStatus };
  /** null is the All tab. */
  categoryId: number | null;
  searchTerm: string;
  /** What the user picked; kept when a build fails. */
  bundle: {
    groupIds: number[];
    includeLoanPlayers: boolean;
    /** Game modes the user doesn't play: plain modes and `live-event:<id>` keys, so new live events start allowed. */
    excludedModes: string[];
  };
  /** The newest saved build, seeded from `latest` on first load. */
  build: {
    data?: ObjectiveBundle;
    status: APIStatus;
    errorCode?: string;
    seeded: boolean;
  };
  /**
   * The plan page's bundle; `stackedPlanStatus` tracks its stacked plan request,
   * `squadsErrorCode` a rejected "Build Objective Squads", `progressFailed`
   * a progress save that was rolled back, `revealStatus` the Reveal Plan request.
   */
  currentBundle: {
    data?: ObjectiveBundle;
    status: APIStatus;
    stackedPlanStatus: APIStatus;
    revealStatus?: APIStatus;
    squadsErrorCode?: string;
    progressFailed?: boolean;
  };
};

/** Customize's plain game modes, in the planner's mode order. */
export const OBJECTIVE_FILTER_MODES = [
  "squad-battles",
  "rivals",
  "champions",
  "rush",
  "co-op",
  "friendlies",
  "draft",
] as const;

/**
 * Whether every match challenge of the group (references carry their group's
 * entries) can be played without the excluded modes: `any` needs any allowed
 * key, `live-event` any allowed live event. Mirrors the planner's rule.
 */
export const isGroupPlayable = (
  matchModes: string[][],
  excludedModes: string[],
  liveEvents: ObjectiveLiveEvent[],
) => {
  const excluded = new Set(excludedModes);
  const eventKeys = liveEvents.map((e) => `live-event:${e.id}`);
  const anyEvent =
    eventKeys.length === 0 || eventKeys.some((k) => !excluded.has(k));
  const anyKey =
    anyEvent || OBJECTIVE_FILTER_MODES.some((m) => !excluded.has(m));
  return matchModes.every((keys) =>
    keys.some((key) =>
      key === "any"
        ? anyKey
        : key === "live-event"
          ? anyEvent
          : !excluded.has(key),
    ),
  );
};

export const DEFAULT_BUNDLE: ObjectivesState["bundle"] = {
  groupIds: [],
  includeLoanPlayers: true,
  excludedModes: [],
};

/** Whether Customize holds anything away from its defaults; its button shows a dot. */
export const hasCustomPlanSettings = (settings: { excludedModes: string[] }) =>
  settings.excludedModes.length > 0;

const initialState: ObjectivesState = {
  groups: { data: [], liveEvents: [], categories: [], status: "idle" },
  currentGroup: { status: "idle" },
  categoryId: null,
  searchTerm: "",
  bundle: DEFAULT_BUNDLE,
  build: { status: "idle", seeded: false },
  currentBundle: { status: "idle", stackedPlanStatus: "idle" },
};

export const fetchObjectiveGroups = createAsyncThunk(
  "objectives/fetchGroups",
  getObjectiveGroups,
);

export const fetchObjectiveGroup = createAsyncThunk(
  "objectives/fetchGroup",
  getObjectiveGroup,
);

// Once per session: the newest build is the current bundle.
export const fetchLatestObjectiveBundle = createAsyncThunk(
  "objectives/fetchLatestBundle",
  getLatestObjectiveBundle,
  {
    condition: (_, { getState }) =>
      !(getState() as IRootState).objectivesReducer.build.seeded,
  },
);

export const fetchObjectiveBundle = createAsyncThunk(
  "objectives/fetchBundle",
  getObjectiveBundle,
);

// The stacked plan is slow to solve, so it is built only when first viewed.
export const fetchStackedPlan = createAsyncThunk(
  "objectives/fetchStackedPlan",
  buildObjectiveBundleStackedPlan,
);

/**
 * Polls the shown bundle's squad run for the blocks it doesn't have yet. No
 * run (the bundle was rebuilt meanwhile) refetches the whole bundle.
 */
export const pollObjectiveSquads = createAsyncThunk<
  {
    id: string;
    view: ObjectivePlanView;
    from: number;
    run: ObjectiveSquadRun | null;
  },
  { id: string; view: ObjectivePlanView },
  { state: IRootState }
>("objectives/pollSquads", async ({ id, view }, { getState, dispatch }) => {
  const blocks =
    getState().objectivesReducer.currentBundle.data?.squads[view]?.blocks ?? [];
  const settled = blocks.indexOf(null);
  const from = settled === -1 ? blocks.length : settled;
  const run = await getObjectiveSquads(id, view, from);
  if (!run) dispatch(fetchObjectiveBundle(id));
  return { id, view, from, run };
});

export const revealPlan = createAsyncThunk(
  "objectives/revealPlan",
  revealObjectiveBundle,
);

/** The tick moves once the backend has stored the new mode. */
export const changeBlockMode = createAsyncThunk(
  "objectives/changeBlockMode",
  async ({
    id,
    view,
    block,
    mode,
  }: {
    id: string;
    view: ObjectivePlanView;
    block: number;
    mode: string;
  }) => setObjectiveBlockMode(id, view, block, mode),
);

export const markSquadsSaved = createAsyncThunk(
  "objectives/markSquadsSaved",
  async ({ id, view }: { id: string; view: ObjectivePlanView }) => {
    await markObjectiveSquadsSaved(id, view);
    return { id, view };
  },
);

/** Shows the view at once; the save runs behind it. */
export const changeObjectiveView = createAsyncThunk(
  "objectives/changeView",
  async ({ id, view }: { id: string; view: ObjectivePlanView }) => {
    await setObjectiveBundleView(id, view);
  },
);

export const requestObjectiveSquads = createAsyncThunk<
  ObjectiveBundle,
  {
    id: string;
    view: ObjectivePlanView;
    settings: ObjectiveSquadSettings;
    keepBlocks?: number[];
  },
  { rejectValue: string | undefined }
>(
  "objectives/startSquads",
  async ({ id, view, settings, keepBlocks }, { rejectWithValue }) => {
    try {
      return await startObjectiveSquads(id, view, settings, keepBlocks);
    } catch (error) {
      return rejectWithValue(backendErrorCode(error));
    }
  },
);

const postObjectiveBundle = createAsyncThunk<
  ObjectiveBundle | null,
  void,
  { state: IRootState; rejectValue: string | undefined }
>(
  "objectives/buildBundle",
  async (_, { getState, signal, rejectWithValue }) => {
    try {
      return await buildObjectiveBundle(
        getState().objectivesReducer.bundle,
        signal,
      );
    } catch (error) {
      return rejectWithValue(backendErrorCode(error));
    }
  },
);

let inFlightBuild: { abort: () => void } | undefined;

type ProgressChange = {
  id: string;
  view: ObjectivePlanView;
  progress: number[][];
};

/** Per `id/view`: the debounced save and the counts saved before it. */
const pendingProgress = new Map<
  string,
  { timer: ReturnType<typeof setTimeout>; saved: number[][] }
>();

const patchObjectiveProgress = createAsyncThunk<
  void,
  ProgressChange & { saved: number[][] }
>("objectives/saveProgress", ({ id, view, progress }) =>
  saveObjectiveBundleProgress(id, view, progress),
);

/**
 * Shows the counts at once and saves them ~500 ms after the last change, so a
 * run of taps is one request. A failed save rolls back to the saved counts.
 */
export const changeObjectiveProgress =
  (change: ProgressChange) =>
  (dispatch: AppDispatch, getState: () => IRootState) => {
    const key = `${change.id}/${change.view}`;
    const pending = pendingProgress.get(key);
    const saved =
      pending?.saved ??
      getState().objectivesReducer.currentBundle.data?.progress[change.view] ??
      [];
    clearTimeout(pending?.timer);
    dispatch(objectivesSlice.actions.setProgress(change));
    pendingProgress.set(key, {
      saved,
      timer: setTimeout(() => {
        pendingProgress.delete(key);
        dispatch(patchObjectiveProgress({ ...change, saved }));
      }, 500),
    });
  };

type BundleChange =
  | { type: "toggle"; groupId: number }
  // The plan page sends the bundle it shows, so a change there never depends
  // on this slice having loaded the latest bundle.
  | { type: "set"; bundle: ObjectivesState["bundle"] };

/** Applies the change, then rebuilds; a newer build aborts the one in flight. */
export const changeObjectiveBundle =
  (change: BundleChange) => (dispatch: AppDispatch) => {
    dispatch(objectivesSlice.actions.applyBundleChange(change));
    // A rebuild clears progress; a save landing after it would bring it back.
    for (const { timer } of pendingProgress.values()) clearTimeout(timer);
    pendingProgress.clear();
    inFlightBuild?.abort();
    const build = dispatch(postObjectiveBundle());
    inFlightBuild = build;
    return build;
  };

/**
 * A re-read of the shown build (squad polling, the stacked plan) keeps the
 * page's counts and view: they may hold taps not saved yet (the stacked plan
 * request races the view save it follows). A rebuild replaces them.
 */
const keepProgress = (
  state: ObjectivesState,
  bundle: ObjectiveBundle,
): ObjectiveBundle =>
  state.currentBundle.data?.id === bundle.id
    ? {
        ...bundle,
        progress: state.currentBundle.data.progress,
        view: state.currentBundle.data.view,
      }
    : bundle;

const objectivesSlice = createSlice({
  name: "objectives",
  initialState,
  reducers: {
    updateObjectiveCategory(state, action: PayloadAction<number | null>) {
      state.categoryId = action.payload;
    },
    updateObjectiveSearchTerm(state, action: PayloadAction<string>) {
      state.searchTerm = action.payload;
    },
    applyBundleChange(state, action: PayloadAction<BundleChange>) {
      const change = action.payload;
      state.build.seeded = true;
      if (change.type === "set") {
        state.bundle = change.bundle;
        return;
      }
      const { groupIds } = state.bundle;
      state.bundle.groupIds = groupIds.includes(change.groupId)
        ? groupIds.filter((id) => id !== change.groupId)
        : [...groupIds, change.groupId];
    },
    setProgress(state, action: PayloadAction<ProgressChange>) {
      const { id, view, progress } = action.payload;
      state.currentBundle.progressFailed = false;
      for (const bundle of [state.currentBundle.data, state.build.data])
        if (bundle?.id === id) bundle.progress[view] = progress;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchObjectiveGroups.pending, (state) => {
        state.groups.status = "pending";
      })
      .addCase(fetchObjectiveGroups.fulfilled, (state, action) => {
        state.groups = {
          data: action.payload.groups,
          liveEvents: action.payload.liveEvents,
          categories: action.payload.categories,
          status: "fulfilled",
          progressUpdatedAt: action.payload.progressUpdatedAt,
        };
      })
      .addCase(fetchObjectiveGroups.rejected, (state) => {
        state.groups.status = "rejected";
      })
      .addCase(fetchObjectiveGroup.pending, (state, action) => {
        // Keep the group already shown only when it is the one being refetched.
        if (state.currentGroup.data?.id !== action.meta.arg)
          state.currentGroup.data = undefined;
        state.currentGroup.status = "pending";
      })
      .addCase(fetchObjectiveGroup.fulfilled, (state, action) => {
        state.currentGroup = { data: action.payload, status: "fulfilled" };
      })
      .addCase(fetchObjectiveGroup.rejected, (state) => {
        state.currentGroup = { status: "rejected" };
      })
      .addCase(fetchLatestObjectiveBundle.fulfilled, (state, action) => {
        // A build started while this was loading is newer.
        if (state.build.seeded) return;
        state.build = {
          data: action.payload ?? undefined,
          status: "fulfilled",
          seeded: true,
        };
        if (action.payload) {
          const {
            groupIds,
            includeLoanPlayers = true,
            excludedModes = [],
          } = action.payload;
          state.bundle = { groupIds, includeLoanPlayers, excludedModes };
        }
      })
      .addCase(fetchLatestObjectiveBundle.rejected, (state) => {
        state.build.seeded = true;
      })
      .addCase(postObjectiveBundle.pending, (state) => {
        state.build.status = "pending";
        state.build.errorCode = undefined;
      })
      .addCase(postObjectiveBundle.fulfilled, (state, action) => {
        state.build.data = action.payload ?? undefined;
        state.build.status = "fulfilled";
        // The bundle is rebuilt in place, so an open plan page shows the new
        // plan; its stacked plan was dropped and is built again when viewed.
        if (
          action.payload &&
          state.currentBundle.data?.id === action.payload.id
        )
          state.currentBundle = {
            ...state.currentBundle,
            data: action.payload,
            stackedPlanStatus: "idle",
          };
      })
      .addCase(postObjectiveBundle.rejected, (state, action) => {
        if (action.meta.aborted) return;
        state.build.data = undefined;
        state.build.status = "rejected";
        state.build.errorCode = action.payload;
      })
      .addCase(fetchObjectiveBundle.pending, (state, action) => {
        if (state.currentBundle.data?.id !== action.meta.arg) {
          state.currentBundle.data = undefined;
          state.currentBundle.stackedPlanStatus = "idle";
        }
        state.currentBundle.status = "pending";
      })
      .addCase(fetchObjectiveBundle.fulfilled, (state, action) => {
        // A stacked plan fetched meanwhile is newer than this read.
        if (
          state.currentBundle.data?.stackedPlan &&
          !action.payload.stackedPlan
        )
          return;
        state.currentBundle.data = keepProgress(state, action.payload);
        state.currentBundle.status = "fulfilled";
      })
      .addCase(fetchObjectiveBundle.rejected, (state) => {
        state.currentBundle = { status: "rejected", stackedPlanStatus: "idle" };
      })
      .addCase(fetchStackedPlan.pending, (state) => {
        state.currentBundle.stackedPlanStatus = "pending";
      })
      .addCase(fetchStackedPlan.fulfilled, (state, action) => {
        state.currentBundle.stackedPlanStatus = "fulfilled";
        if (state.currentBundle.data?.id === action.payload.id)
          state.currentBundle.data = keepProgress(state, action.payload);
        if (state.build.data?.id === action.payload.id)
          state.build.data = action.payload;
      })
      .addCase(fetchStackedPlan.rejected, (state) => {
        state.currentBundle.stackedPlanStatus = "rejected";
      })
      .addCase(pollObjectiveSquads.fulfilled, (state, action) => {
        const { id, view, from, run } = action.payload;
        const bundle = state.currentBundle.data;
        const current = bundle?.squads[view];
        // Dropped when a rebuild or a new run replaced the one polled.
        if (!run || bundle?.id !== id || !current) return;
        bundle.squads[view] = {
          status: run.status,
          blocks: [...current.blocks.slice(0, from), ...run.blocks],
        };
      })
      .addCase(revealPlan.pending, (state) => {
        state.currentBundle.revealStatus = "pending";
      })
      .addCase(revealPlan.fulfilled, (state, action) => {
        state.currentBundle.revealStatus = "fulfilled";
        if (state.currentBundle.data?.id === action.payload.id)
          state.currentBundle.data = keepProgress(state, action.payload);
        if (state.build.data?.id === action.payload.id)
          state.build.data = action.payload;
      })
      .addCase(revealPlan.rejected, (state) => {
        state.currentBundle.revealStatus = "rejected";
      })
      .addCase(changeBlockMode.fulfilled, (state, action) => {
        if (state.currentBundle.data?.id === action.payload.id)
          state.currentBundle.data = keepProgress(state, action.payload);
        if (state.build.data?.id === action.payload.id)
          state.build.data = action.payload;
      })
      .addCase(changeObjectiveView.pending, (state, action) => {
        const { id, view } = action.meta.arg;
        if (state.currentBundle.data?.id === id)
          state.currentBundle.data.view = view;
        if (state.build.data?.id === id) state.build.data.view = view;
      })
      .addCase(markSquadsSaved.fulfilled, (state, action) => {
        const { id, view } = action.payload;
        if (state.currentBundle.data?.id === id)
          state.currentBundle.data.squadsSaved[view] = true;
      })
      .addCase(requestObjectiveSquads.pending, (state) => {
        state.currentBundle.squadsErrorCode = undefined;
      })
      .addCase(requestObjectiveSquads.fulfilled, (state, action) => {
        if (state.currentBundle.data?.id === action.payload.id)
          state.currentBundle.data = keepProgress(state, action.payload);
      })
      .addCase(requestObjectiveSquads.rejected, (state, action) => {
        state.currentBundle.squadsErrorCode = action.payload ?? "generic";
      })
      .addCase(patchObjectiveProgress.rejected, (state, action) => {
        const { id, view, saved } = action.meta.arg;
        if (state.currentBundle.data?.id !== id) return;
        state.currentBundle.data.progress[view] = saved;
        state.currentBundle.progressFailed = true;
      });
  },
});

export const getObjectivesSelector = (state: IRootState) =>
  state.objectivesReducer;

// Categories the loaded groups use, in EA's order; ids without a stored name yet go last.
export const getObjectiveCategoriesSelector = createSelector(
  [
    (state: IRootState) => state.objectivesReducer.groups.data,
    (state: IRootState) => state.objectivesReducer.groups.categories,
  ],
  (groups, categories): { id: number; name?: string }[] => {
    const named = new Set(categories.map((category) => category.id));
    const unnamed = [...new Set(groups.map((group) => group.categoryId))]
      .filter((id) => !named.has(id))
      .sort((a, b) => a - b);
    return [...categories, ...unnamed.map((id) => ({ id }))];
  },
);

export const getFilteredObjectiveGroupsSelector = createSelector(
  [
    (state: IRootState) => state.objectivesReducer.groups.data,
    (state: IRootState) => state.objectivesReducer.categoryId,
    (state: IRootState) => state.objectivesReducer.searchTerm,
  ],
  (groups, categoryId, searchTerm) =>
    groups.filter(
      (group) =>
        (categoryId === null || group.categoryId === categoryId) &&
        (!searchTerm || includesIgnoringAccents(group.name, searchTerm)),
    ),
);

export const isInObjectiveBundleSelector =
  (groupId: number) => (state: IRootState) =>
    state.objectivesReducer.bundle.groupIds.includes(groupId);

export const {
  updateObjectiveCategory,
  updateObjectiveSearchTerm,
  applyBundleChange,
} = objectivesSlice.actions;

export default objectivesSlice.reducer;
